import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  PageContentRewards,
  Sheet,
  SheetContentProduct,
  OptionRow,
  OfferWarningType,
  useCartStore,
  useAuthStore,
} from '@restaurant/core'
import { useWebRewardsPageViewModel } from '../viewmodels/pages/WebRewardsPageViewModel'
import { useOfferAlerts } from '../hooks/useOfferAlerts'
import { showcaseBlocked } from '../showcase/guard'

export default function RewardsPage() {
  const navigate = useNavigate()
  // The history route is auth-gated (ProtectedRoute -> /menu), so only offer
  // the control to a signed-in user; otherwise the button bounces them.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const {
    balance,
    merchantConfig,
    rewardGroups,
    isEmpty,
    handleRewardClick,
    pendingRewardProduct,
    confirmRewardProduct,
    cancelRewardProduct,
  } = useWebRewardsPageViewModel()

  let pointsUntilNextReward: number | undefined
  if (merchantConfig) {
    if (merchantConfig.redemption_type === 'item' && merchantConfig.item_redemption_groups) {
      const nextThreshold = merchantConfig.item_redemption_groups
        .map(g => g.point_value)
        .filter(v => v > balance)
        .sort((a, b) => a - b)[0]
      if (nextThreshold != null) pointsUntilNextReward = nextThreshold - balance
    } else if (merchantConfig.redemption_type === 'dollar' && merchantConfig.point_value) {
      const remainder = balance % merchantConfig.point_value
      pointsUntilNextReward = remainder === 0 ? 0 : merchantConfig.point_value - remainder
    }
  }

  const offerAlert = useOfferAlerts()
  const removeAppliedOffer = useCartStore((s) => s.removeAppliedOffer)

  const handleRewardClickWithConflict = useCallback(
    (_reward: unknown, groupIndex: number, rewardIndex: number) => {
      if (showcaseBlocked()) return
      const result = handleRewardClick(groupIndex, rewardIndex)
      if (result.blocked && result.reason === 'coupon_conflict') {
        offerAlert.showWarning(
          OfferWarningType.offerAndRewardNotAllowed,
          null,
          {
            onPrimary: () => {}, // "KEEP OFFER" — just dismiss
            onSecondary: () => {
              removeAppliedOffer()
              // Retry after removal
              handleRewardClick(groupIndex, rewardIndex)
            },
          }
        )
      }
    },
    [handleRewardClick, offerAlert, removeAppliedOffer]
  )

  // Customization state for the product detail sheet
  const [selectedCustomizations, setSelectedCustomizations] = useState<Record<string, Set<string>>>({})

  // Reset customization state when a new pending product appears
  const menuItem = pendingRewardProduct?.menuItem
  const isProductDetailOpen = !!pendingRewardProduct

  // Initialize customizations when sheet opens
  const handleSheetOpen = useCallback(() => {
    if (!menuItem?.customizations) return
    const initial: Record<string, Set<string>> = {}
    menuItem.customizations.forEach((c) => {
      const defaults = c.options.filter((o) => o.isDefault).map((o) => o.id)
      if (defaults.length > 0) {
        initial[c.id] = new Set(defaults)
      }
    })
    setSelectedCustomizations(initial)
  }, [menuItem])

  useEffect(() => {
    if (pendingRewardProduct) {
      handleSheetOpen()
    }
  }, [pendingRewardProduct, handleSheetOpen])

  const handleOptionChange = useCallback(
    (customizationId: string, optionId: string, selected: boolean, isRadio: boolean) => {
      setSelectedCustomizations((prev) => {
        const next = { ...prev }
        if (isRadio) {
          next[customizationId] = new Set([optionId])
        } else {
          const current = new Set(prev[customizationId] || [])
          if (selected) current.add(optionId)
          else current.delete(optionId)
          next[customizationId] = current
        }
        return next
      })
    },
    []
  )

  const missingRequirements = useMemo(() => {
    if (!menuItem?.customizations) return undefined
    const missing = menuItem.customizations.filter((c) => {
      if (!c.isRequired) return false
      const selections = selectedCustomizations[c.id] || new Set()
      return selections.size === 0
    }).length
    if (missing === 0) return undefined
    if (missing === 1) return 'Select 1 required option'
    return `Select ${missing} required options`
  }, [menuItem, selectedCustomizations])

  const optionGroups = useMemo(() => {
    if (!menuItem?.customizations) return []
    return menuItem.customizations.map((customization) => {
      const isRadio = customization.maxSelections === 1
      const selections = selectedCustomizations[customization.id] || new Set()
      return {
        id: customization.id,
        title: customization.name,
        required: customization.isRequired,
        maxSelections: customization.maxSelections,
        children: (
          <>
            {customization.options.map((option) => (
              <OptionRow
                key={option.id}
                text={option.name}
                optionType={isRadio ? 'radio' : 'checkbox'}
                value={option.id}
                name={customization.id}
                selected={selections.has(option.id)}
                onChange={(value, selected) => handleOptionChange(customization.id, value, selected, isRadio)}
              />
            ))}
          </>
        ),
      }
    })
  }, [menuItem, selectedCustomizations, handleOptionChange])

  const handleAddToOrder = useCallback(() => {
    const customizationSelections = Object.entries(selectedCustomizations).flatMap(
      ([customizationItemId, optionIds]) =>
        Array.from(optionIds).map((selectedOptionId) => ({
          customizationItemId,
          selectedOptionId,
        }))
    )
    confirmRewardProduct(customizationSelections)
    setSelectedCustomizations({})
  }, [selectedCustomizations, confirmRewardProduct])

  const handleClose = useCallback(() => {
    cancelRewardProduct()
    setSelectedCustomizations({})
  }, [cancelRewardProduct])

  if (isEmpty) {
    return (
      <PageContentRewards
        rewardGroups={[]}
        showDefaultGroups={false}
        points={balance}
        pointsUntilNextLevel={pointsUntilNextReward}
        loading={false}
        emptyStateContent={
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No rewards available</p>
            <p className="text-sm text-gray-500">
              Earn points by making purchases to unlock rewards
            </p>
          </div>
        }
        onHistoryPress={isAuthenticated ? () => navigate('/rewards/history') : undefined}
        testID="rewards-page"
        accessibilityLabel="Rewards program with point tracking and available rewards"
      />
    )
  }

  return (
    <>
      <PageContentRewards
        rewardGroups={rewardGroups}
        showDefaultGroups={false}
        points={balance}
        pointsUntilNextLevel={pointsUntilNextReward}
        loading={false}
        onRewardClick={handleRewardClickWithConflict}
        onHistoryPress={isAuthenticated ? () => navigate('/rewards/history') : undefined}
        testID="rewards-page"
        accessibilityLabel={`Rewards program - ${balance} points available`}
      />

      {/* Offer/reward conflict alert dialog.
          Required-prop fallbacks: alertProps is Partial<AlertProps>; when the
          hook is inactive it returns { visible: false } and Alert renders null,
          so these placeholders are never displayed. Spread wins when active. */}
      <Alert
        title=""
        description=""
        primaryAction={{ text: '', onPress: () => {} }}
        {...offerAlert.alertProps}
      />

      {/* Product detail sheet for reward items with required customizations */}
      <Sheet
        isOpen={isProductDetailOpen}
        onClose={handleClose}
        title={menuItem?.name || 'Reward Item'}
        testID="reward-product-detail-sheet"
      >
        {menuItem && (
          <SheetContentProduct
            showSectionRequiredProduct={false}
            sectionProductInformation={{
              productName: menuItem.name,
              description: menuItem.description || '',
              currentPrice: 'FREE',
              imageUrl: menuItem.imageUrl,
            }}
            optionGroups={optionGroups}
            sectionAddToOrder={{
              price: 'FREE',
              quantity: 1,
              onQuantityChange: () => {},
              isFavorited: false,
              onFavoriteToggle: () => {},
              missingRequirements,
              onAddToOrder: handleAddToOrder,
            }}
            testID="reward-product-detail-content"
          />
        )}
      </Sheet>
    </>
  )
}
